import { Modal, Input, Table, Select, Tag, Button } from "antd";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CloseOutlined } from "@ant-design/icons";
import moment from "moment";
import { http } from "../../services/http";
import EmployeesModal from "./Employees";
import { numberWithSpacesIntl } from "../../utils/numberWithSpaces";
import CarIcon from "../../assets/icons/CarIcons";
import CarIcon2 from "../../assets/icons/CarIconV2";
import LoadersIcon from "../../assets/icons/LoadersIcon";
import {
  driverPdf,
  driverPdfExact,
  loadersPdf,
} from "../../utils/navigateToPDF";

const FinishedSalesModalWrh = ({ visible, data, onClose }) => {
  const { t } = useTranslation();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedComments, setSelectedComments] = useState("");
  const [responsibleData, setResponsibleData] = useState([]);
  const [status, setStatus] = useState("");
  const [salesData, setSalesData] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [responsible, setResponsible] = useState("");
  const [isEmployeeModalVisible, setIsEmployeeModalVisible] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [loaders, setLoaders] = useState([]);

  const orderStatus = [
    { label: "Barchasi", value: "" },
    { label: "Tasdiqlandi", value: "Tasdiqlandi" },
    { label: "Mas'ulga biriktirildi", value: "Biriktirildi" },
    { label: "Buyurtma yuklandi", value: "BuyurtmaYuklandi" },
    { label: "Yuklash tasdiqlandi", value: "YuklashTasdiqlandi" },
  ];

  const handleSelectClick = () => {
    setSelectedComments(data?.comments);
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const fetchResponsibleManagers = async () => {
    try {
      const { data } = await http.get(
        `api/sales/warehouse-employee-info?skip=0`,
      );

      setResponsibleData(data.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchOnHandQuantity = async (itemCodes, fromWhs) => {
    try {
      const { data } = await http.get(
        `api/items/ombordagiitems/on-hand-items?itemCodes=${itemCodes}&whsCode=${fromWhs}`,
      );
      return data;
    } catch (error) {
      console.log("Error:", error.message);
    }
  };

  const getLoaders = async (docEntry) => {
    if (!docEntry || docEntry === "" || docEntry === null) {
      setLoaders([]);
      return;
    }
    try {
      const { data: loadersData } = await http.get(
        `api/deliverynotesdata/get-employees?docEntry=${docEntry}`,
      );
      const resData = loadersData[0]?.documentLines || [];
      const loaderNames = resData?.map((item) => item?.employeeName).join(", ");
      const loaderIds = resData?.map((item) => item?.employeeID) || [];

      setLoaders(loaderIds);
      setPdfData((prevState) => ({
        ...prevState,
        loaderNames: loaderNames,
      }));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchResponsibleManagers();
  }, []);

  useEffect(() => {
    if (data) {
      fetchByDocEntry(data.docEntry);

      // Fetch loaders if u_DeliveryID exists
      if (data?.u_DeliveryID) {
        getLoaders(data.u_DeliveryID);
      }

      // Prepare PDF data with onHand quantities
      if (data?.documentLines) {
        const itemCodes = data.documentLines
          .map((item) => item.itemCode)
          .join(",");

        (async () => {
          const fetchedData = await fetchOnHandQuantity(
            itemCodes,
            data?.documentLines[0]?.warehouseCode,
          );

          const mappedLines = data.documentLines.map((item) => {
            const onHandItem = fetchedData?.find(
              (fetchedItem) => fetchedItem.itemCode === item.itemCode,
            );

            return {
              ...item,
              whsOnHandQuantity: onHandItem ? onHandItem.onHand : 0,
              whsAvailableQuantity: onHandItem ? onHandItem.available : 0,
            };
          });

          setPdfData({
            ...data,
            documentLines: mappedLines,
          });
        })();
      } else {
        setPdfData(data);
      }
    }
  }, [data]);

  const fetchByDocEntry = async (docEntry) => {
    setSalesLoading(true);
    try {
      const { data } = await http.get(`api/sales/completed-order/${docEntry}`);
      const resData = data.data;
      const status = resData.orderStatus
        ? orderStatus.find((status) => {
            return status.value === resData.orderStatus;
          })
        : "-";
      setStatus(status.label);
      setSalesData(resData);
      const responsiblePerson = resData.responsiblePerson
        ? responsibleData.find((item) => {
            return item.employeeId === Number(resData.responsiblePerson);
          })
        : "-";
      setResponsible(responsiblePerson);

      console.log("Status: ", status);
    } catch (err) {
      console.log(err);
    } finally {
      setSalesLoading(false);
    }
  };

  const handleButtonClickEmployee = (e) => {
    e.stopPropagation();
    setIsEmployeeModalVisible(true);
  };

  if (!data) return null;

  const sum =
    data.docCur === "UZS"
      ? new Intl.NumberFormat("fr-FR").format(data.docTotalFC)
      : new Intl.NumberFormat("fr-FR").format(data.docTotal);

  const columns = [
    {
      title: t("item"),
      dataIndex: "itemDescription",
      key: "itemDescription",
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      key: "quantity",
      render: (text, record) => {
        const formattedText = new Intl.NumberFormat("fr-FR").format(text);
        return `${formattedText} `;
      },
    },
    {
      title: t("measureUnit"),
      dataIndex: "measureUnit",
      key: "measureUnit",
    },
    {
      title: t("price"),
      dataIndex: "price",
      key: "price",
      render: (text, record, index) => {
        const formattedText = new Intl.NumberFormat("fr-FR").format(text);
        return (
          <span
            className={`${record.lineStatus === "C" ? "text-slate-500" : "text-black"}`}
          >
            {formattedText} {record.currency}
          </span>
        );
      },
    },
    {
      title: t("weight"),
      dataIndex: "weight",
      key: "weight",
      render: (text, record, index) => {
        return (
          <span
            className={`${record.lineStatus === "C" ? "text-slate-500" : "text-black"}`}
          >
            {`${numberWithSpacesIntl(text * record.quantity)} кг`}
          </span>
        );
      },
    },
    {
      title: t("Hajmi"),
      dataIndex: "volume",
      key: "volume",
      render: (text, record, index) => {
        return (
          <span
            className={`${record.lineStatus === "C" ? "text-slate-500" : "text-black"}`}
          >
            {`${numberWithSpacesIntl(text * record.quantity)} `}
          </span>
        );
      },
    },
    {
      title: t("packageQty"),
      dataIndex: "packageQty",
      key: "packageQty",
      render: (text, record, index) => {
        let packageQty;
        if (!record.packageQty || record.packageQty > record.quantity) {
          packageQty = 0;
        } else {
          packageQty = record.quantity / record.packageQty;
        }
        return (
          <span
            className={`${record.lineStatus === "C" ? "text-slate-500" : "text-black"}`}
          >
            {`${numberWithSpacesIntl(packageQty.toFixed(1))} `}
          </span>
        );
      },
    },
    {
      title: t("totalMiqdor"),
      dataIndex: "lineTotal",
      key: "lineTotal",
      render: (text, record) => {
        const formatSum =
          record.currency === "UZS"
            ? new Intl.NumberFormat("fr-FR").format(record?.totalFrgn)
            : new Intl.NumberFormat("fr-FR").format(text);
        return (
          <span
            className={`${record.lineStatus === "C" ? "text-slate-500" : "text-black"}`}
          >
            {`${formatSum} ${record.currency}`}
          </span>
        );
      },
    },
  ];

  const tableData = data.documentLines.map((item, index) => ({
    key: index,
    itemDescription: item.itemDescription,
    quantity: item.quantity,
    measureUnit: item.measureUnit,
    price: item.price,
    inventoryQuantity: item.inventoryQuantity,
    lineTotal: item.lineTotal,
    ugpName: item.ugpName,
    whsName: item.whsName,
    currency: item.currency,
    totalFrgn: item.totalFrgn,
    ...item,
  }));

  return (
    <Modal
      title={
        <h1 className="font-nunito text-xl font-extrabold text-[#000000]">
          {t("order")} № {data.docNum}
        </h1>
      }
      visible={visible}
      onCancel={onClose}
      footer={null}
      closeIcon={<CloseOutlined />}
      width="80%"
      style={{ height: "70%" }}
      bodyStyle={{ height: "calc(100% - 55px)", overflowY: "auto" }}
    >
      <div className="w-full px-4 sm:px-14">
        <div className="w-full">
          <div className="mt-10 flex flex-col items-start justify-between sm:flex-row">
            <div className="flex flex-col gap-5">
              <div className="flex w-full flex-col gap-2 sm:w-auto">
                <p className="font-nunito">{t("mijoz")}</p>
                <Input
                  type="text"
                  value={data?.cardName}
                  className="flex h-10 w-full items-center justify-center font-bold text-black sm:w-[400px]"
                  style={{ color: "black" }}
                  disabled
                />
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto">
                <p className="font-nunito">{t("agent")}</p>
                <Input
                  type="text"
                  value={data?.slpName}
                  className="flex h-10 w-full items-center justify-center font-bold text-black sm:w-[400px]"
                  style={{ color: "black" }}
                  disabled
                />
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto">
                <p className="font-nunito">{t("whs")}</p>
                <Input
                  type="text"
                  value={`${tableData[0]?.whsName} - ${tableData[0]?.warehouseCode}`}
                  className="flex h-10 w-full items-center justify-center font-bold text-black sm:w-[400px]"
                  style={{ color: "black" }}
                  disabled
                />
              </div>
            </div>
            <div className="flex w-full flex-col gap-3 sm:w-auto">
              <div className="flex w-full flex-col gap-2 sm:w-auto">
                <p className="font-nunito">{t("createdDate")}</p>

                <Input
                  type="text"
                  value={moment(data?.docDate).format("DD.MM.YYYY")}
                  style={{ color: "black" }}
                  disabled
                />
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto">
                <p className="font-nunito">{t("shippingDate")}</p>

                <Input
                  type="text"
                  value={moment(data?.dueDate).format("DD.MM.YYYY")}
                  style={{ color: "black" }}
                  disabled
                />
              </div>
            </div>
          </div>
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={false}
            className="border-1 mt-12 border-black"
            scroll={{ x: "100%" }}
          />

          <div className="mt-10 flex items-center justify-between">
            <div className="flex flex-col gap-3">
              <p>{t("comments")}</p>
              <Select
                value={data.comments}
                className="flex h-12 w-full items-center justify-center sm:w-[250px]"
                style={{ color: "black" }}
                disabled
                onClick={handleSelectClick}
              />
              <Modal
                title={t("comments")}
                visible={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
              >
                <p>{selectedComments}</p>
              </Modal>
            </div>
            <div className="flex items-center justify-end gap-5">
              <div className="flex flex-col gap-3">
                <p>{t("docTotalQuantity")}</p>
                <Input
                  type="text"
                  value={`${data.docTotalQuantity} ${data.documentLines[0].measureUnit}`}
                  style={{ color: "black" }}
                  className="flex h-10 w-full items-center justify-center border-[1px] border-[#D0D0D0] active:border-[#D0D0D0] sm:w-[200px]"
                  disabled
                />
              </div>

              <div className="flex flex-col gap-3">
                <p>{t("totalPrice")}</p>
                <Input
                  type="text"
                  value={`${sum} ${data.docCur}`}
                  style={{ color: "black" }}
                  className="flex h-10 w-full items-center justify-center border-[1px] border-[#D0D0D0] active:border-[#D0D0D0] sm:w-[200px]"
                  disabled
                />
              </div>
            </div>
          </div>
          <div className="mt-10 flex flex-col items-center justify-between gap-3 sm:flex-row">
            <div className="mt-4 flex flex-col gap-3 sm:mt-0">
              <p>{t("driver")}</p>
              <Input
                type="text"
                value={data.driverCardName}
                disabled
                style={{ color: "black" }}
                className="flex h-10 w-[180px] items-center justify-center border-[1px] border-[#D0D0D0]"
              />
            </div>
            <div className="mt-4 flex flex-col gap-3 sm:mt-0">
              <Button
                onClick={(e) => handleButtonClickEmployee(e)}
                className="h-10 min-w-[120px] bg-[#0A4D68] text-white"
              >
                {t("employee")}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Button
            type="default"
            onClick={() => {
              driverPdf({ data: pdfData });
            }}
            disabled={
              !pdfData ||
              !data?.driver ||
              !data?.u_CarNumber ||
              !loaders ||
              !pdfData?.loaderNames ||
              !loaders.length
            }
            className="h-10 rounded-lg bg-green-500 text-white hover:!bg-green-600 sm:w-auto"
          >
            <CarIcon />
          </Button>
          <Button
            type="default"
            onClick={() => {
              driverPdfExact({ data: pdfData });
            }}
            disabled={
              !pdfData ||
              !data?.driver ||
              !data?.u_CarNumber ||
              !loaders ||
              !pdfData?.loaderNames ||
              !loaders.length
            }
            className="h-10 rounded-lg bg-green-500 text-white hover:!bg-green-600 sm:w-auto"
          >
            <CarIcon2 />
          </Button>

          <Button
            type="default"
            onClick={() => {
              loadersPdf({ data: pdfData });
            }}
            disabled={
              !pdfData || !pdfData?.loaderNames || !loaders || !loaders.length
            }
            className="h-10 rounded-lg bg-yellow-500 text-white hover:!bg-yellow-600 sm:w-auto"
          >
            <LoadersIcon />
          </Button>
        </div>
      </div>
      <EmployeesModal
        visible={isEmployeeModalVisible}
        data={data}
        onClose={() => setIsEmployeeModalVisible(false)}
      />
    </Modal>
  );
};

export default FinishedSalesModalWrh;
